import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

import { customInstance } from "config/mutator";
import {
  campaignTemplateKeys,
  type BulkMutationMeta,
  type CampaignTemplateListQueryParams,
} from "hooks/api/query-keys";

import type {
  CampaignTemplateCreateRequest,
  CampaignTemplateDTO,
  CampaignTemplatePatchRequest,
  CampaignTemplateWithDataDTO,
} from "generated/schemas";

type UseQueryHookOptions<TQueryFnData, TData = TQueryFnData> = Omit<
  UseQueryOptions<TQueryFnData, Error, TData>,
  "queryKey" | "queryFn"
>;

type CampaignTemplateSchemaParams = {
  version?: number;
};

const buildListRequestParams = (
  params?: CampaignTemplateListQueryParams,
): Record<string, unknown> => {
  const requestParams: Record<string, unknown> = {};

  // No explicit size => "fetch all" via unpaged (preserves previous default-size behavior).
  if (params?.size === undefined) {
    requestParams.unpaged = true;
  } else {
    requestParams.size = params.size;
    requestParams.page = params.page ?? 0;
  }

  if (params?.q) {
    requestParams.q = params.q;
  }

  if (params?.sort?.length) {
    requestParams.sort = params.sort;
  }

  if (params?.filters) {
    Object.entries(params.filters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length) {
        requestParams[`${key}.in`] = value;
      }
    });
  }

  return requestParams;
};

const listCampaignTemplates = async (
  params?: CampaignTemplateListQueryParams,
): Promise<CampaignTemplateWithDataDTO[]> => {
  const response = await customInstance<
    CampaignTemplateWithDataDTO[] | { content?: CampaignTemplateWithDataDTO[] }
  >({
    url: "/api/campaign-templates",
    method: "GET",
    params: buildListRequestParams(params),
  });

  if (Array.isArray(response)) {
    return response;
  }

  return response.content ?? [];
};

const listCampaignTemplateSummaries = async (
  params?: CampaignTemplateListQueryParams,
): Promise<CampaignTemplateDTO[]> => {
  const response = await customInstance<CampaignTemplateDTO[] | { content?: CampaignTemplateDTO[] }>({
    url: "/api/campaign-templates/summary",
    method: "GET",
    params: buildListRequestParams(params),
  });

  if (Array.isArray(response)) {
    return response;
  }

  return response.content ?? [];
};

const getCampaignTemplate = (id: string): Promise<CampaignTemplateWithDataDTO> =>
  customInstance<CampaignTemplateWithDataDTO>({
    url: `/api/campaign-templates/${id}`,
    method: "GET",
  });

const createCampaignTemplate = (
  payload: CampaignTemplateCreateRequest,
): Promise<CampaignTemplateWithDataDTO> =>
  customInstance<CampaignTemplateWithDataDTO>({
    url: "/api/campaign-templates",
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: payload,
  });

const updateCampaignTemplate = ({
  id,
  payload,
}: {
  id: string;
  payload: CampaignTemplatePatchRequest;
}): Promise<CampaignTemplateWithDataDTO> =>
  customInstance<CampaignTemplateWithDataDTO>({
    url: `/api/campaign-templates/${id}`,
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    data: payload,
  });

const archiveCampaignTemplate = (id: string): Promise<void> =>
  customInstance<void>({
    url: `/api/campaign-templates/${id}`,
    method: "DELETE",
  });

const getCampaignTemplateSchema = (version: number): Promise<unknown> =>
  customInstance<unknown>({
    url: `/api/campaign-templates/schema/v${version}`,
    method: "GET",
  });

export const useCampaignTemplates = <TData = CampaignTemplateWithDataDTO[]>(
  params?: CampaignTemplateListQueryParams,
  options?: UseQueryHookOptions<CampaignTemplateWithDataDTO[], TData>,
) =>
  useQuery({
    queryKey: campaignTemplateKeys.list(params),
    queryFn: () => listCampaignTemplates(params),
    ...options,
  });

export const useCampaignTemplateSummaries = <TData = CampaignTemplateDTO[]>(
  params?: CampaignTemplateListQueryParams,
  options?: UseQueryHookOptions<CampaignTemplateDTO[], TData>,
) =>
  useQuery({
    queryKey: campaignTemplateKeys.summary(params),
    queryFn: () => listCampaignTemplateSummaries(params),
    ...options,
  });

export const useCampaignTemplate = <TData = CampaignTemplateWithDataDTO>(
  id?: string,
  options?: UseQueryHookOptions<CampaignTemplateWithDataDTO, TData>,
) =>
  useQuery({
    queryKey: campaignTemplateKeys.detail(id ?? ""),
    queryFn: async () => {
      if (!id) {
        throw new Error("Template id required for useCampaignTemplate queryFn");
      }

      return getCampaignTemplate(id);
    },
    ...options,
    enabled: Boolean(id) && (options?.enabled ?? true),
  });

export const useCampaignTemplateSchema = <TData = unknown>(
  params: CampaignTemplateSchemaParams,
  options?: UseQueryHookOptions<unknown, TData>,
) => {
  const { version } = params;

  return useQuery({
    queryKey: campaignTemplateKeys.schema(version ?? 0),
    queryFn: () => {
      if (version == null) {
        throw new Error("Schema version required for campaign template schema");
      }

      return getCampaignTemplateSchema(version);
    },
    ...options,
    enabled: version != null && (options?.enabled ?? true),
  });
};

export const useCreateCampaignTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCampaignTemplate,
    onSuccess: (template) => {
      queryClient.invalidateQueries({ queryKey: campaignTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: campaignTemplateKeys.summaries() });

      if (template.id) {
        queryClient.setQueryData(campaignTemplateKeys.detail(template.id), template);
      }
    },
  });
};

export const useUpdateCampaignTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCampaignTemplate,
    onSuccess: (template, { id }) => {
      queryClient.invalidateQueries({ queryKey: campaignTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: campaignTemplateKeys.summaries() });
      queryClient.setQueryData(campaignTemplateKeys.detail(template.id ?? id), template);
    },
  });
};

export type ArchiveCampaignTemplateVariables = string | { id: string; meta?: BulkMutationMeta };

export const useArchiveCampaignTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ArchiveCampaignTemplateVariables) => {
      const id = typeof payload === "string" ? payload : payload.id;

      return archiveCampaignTemplate(id);
    },
    onSuccess: (_data, variables) => {
      if (typeof variables === "object" && variables?.meta?.skipInvalidation) {
        return;
      }

      const id = typeof variables === "string" ? variables : variables.id;
      queryClient.invalidateQueries({ queryKey: campaignTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: campaignTemplateKeys.summaries() });
      queryClient.removeQueries({ queryKey: campaignTemplateKeys.detail(id) });
    },
  });
};
